import { AnalyticsEventName } from "@albertine/analytics-client";
import { useGet, usePatch, usePost } from "@albertine/api-client";
import {
    type GooglePlace,
    MemberPreferredCommunicationChannel,
    OfferingCategory,
} from "@albertine/types";
import type { RecommendationItem } from "@albertine/types/openapi/lmt";
import * as Sentry from "@sentry/react";
import classNames from "classnames";
import { getDocs } from "firebase/firestore";
import type { PostHog } from "posthog-js";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect } from "react";
import { useHistory } from "react-router";
import { cn } from "@/lib/utils";
import { addressComponentsToObject } from "../../common/CatalogItem";
import type { Agent } from "../../common/types/Agent";
import type { Booking } from "../../common/types/Booking";
import {
    defaultRequestTitle,
    type Request,
    RequestGooglePlaceLocation,
} from "../../common/types/Request";
import { RequestSource, RequestSourceMap } from "../../common/types/RequestSource";
import { googlePlaceToAddressComponents } from "../../common/utils/google/GooglePlaceService";
import { NumberOfGuests, timeToDate } from "../../common/utils/time.util";
import { isTypeOf } from "../../common/utils/zod.util";
import {
    type CreateOneOffBookingInput,
    EventEmitter,
    type EventListener,
} from "../../EventEmitter";
import { queryMessageWithRequest } from "../../FirebaseBackend";
import { uploadImagesToBooking } from "../../FirebaseBackendActions";
import { mapApiBookingToBooking } from "../../utils/booking.adapter";
import type { CreateBookingApiPayload } from "../../utils/booking.util";
import { PREFERRED_CHANNEL_LABELS } from "../../utils/channel.util";
import routes from "../../utils/routes";
import AgentDropdown from "../Common/AgentDropdown";
import AlertNotification from "../Common/AlertNotification";
import Button from "../Common/Buttons";
import { FormattedDateTime } from "../Common/DateTime";
import Dropdown, { type DropdownOption } from "../Common/Dropdown";
import { EditableLocalDateInput } from "../Common/EditableDateInput";
import EditableTextarea from "../Common/EditableTextarea";
import Icon, { PictogramIcon } from "../Common/Icon";
import Input from "../Common/Input";
import Label from "../Common/Label";
import MemberRating from "../Common/rating/MemberRating";
import Tooltip from "../Common/Tooltip";
import GooglePlaceInputField from "../GooglePlaceInputField/GooglePlaceInputField";
import { deriveCaptureView } from "./aiCapture.util";
import InternalNotes from "./InternalNotes";
import Proposal from "./Proposal";
import RequestStatusSelector from "./RequestStatusSelector";
import "./RequestView.css";
import type {
    CatalogItemFull,
    SearchCatalogItemsQuery,
    Tenant,
    UpdateRequestRequest,
} from "@albertine/types/openapi/lmt";
import { AnalysingValue } from "@prototypes/ai-capture/AnalysingPlaceholder";
import { RecommendButton } from "@prototypes/RecommendButton";
import { Lib } from "albertine-shared-web";
import {
    CategoriesWithUncategorized,
    categoryDisplayName,
    OfferingCategoryUncategorizedKey,
} from "../../common/types/OfferingCategory";
import { addressFieldsAndLocationToFilter } from "../../common/utils/catalog.util";
import { timestampFromDateTimeStrings } from "../../utils/time.util";
import { fromTimestampToDate } from "../../utils/timestamp.util";
import { logErrorAndShowToast } from "../../utils/window.util";

function hasGooglePlaceLocation(request: Request) {
    if (
        request.locationGooglePlaceId &&
        request.locationGoogleName &&
        request.locationGoogleFormattedAddress &&
        request.locationGoogleGeometryLat &&
        request.locationGoogleGeometryLng
    ) {
        return true;
    }
    return false;
}

async function addItemFromCatalogToCart(
    catalogItem: CatalogItemFull,
    request: Request,
    bookings: Booking[],
    posthog: PostHog | null,
    createBooking: (payload: CreateBookingApiPayload) => Promise<{ bookingId: string }>,
    recommendation?: {
        fitScore: number;
        rank: number;
    },
) {
    const selectedPhotos: string[] = catalogItem.images
        ? catalogItem.images?.map((i) => i.url)
        : [];

    try {
        const { bookingId: createdBookingId } = await createBooking({
            requestId: request.id,
            catalogItemId: catalogItem.lmtId,
        });

        const isFirstOption = bookings.length === 0;

        if (recommendation) {
            posthog?.capture(AnalyticsEventName.REQUEST_RECOMMENDATIONS_OPTION_ADDED, {
                request_id: request.id,
                option_id: catalogItem.lmtId,
                fit_score: recommendation.fitScore,
                rank: recommendation.rank,
            });

            if (isFirstOption) {
                posthog?.capture(AnalyticsEventName.REQUEST_FIRST_OPTION_ADDED, {
                    request_id: request.id,
                    source: "ai",
                });
            }
        } else if (isFirstOption) {
            posthog?.capture(AnalyticsEventName.REQUEST_FIRST_OPTION_ADDED, {
                request_id: request.id,
                source: "manual",
            });
        }

        try {
            if (selectedPhotos && selectedPhotos.length > 0) {
                await uploadImagesToBooking(createdBookingId, selectedPhotos);
            }
        } catch (error) {
            Sentry.captureException(error, {
                extra: {
                    message: `Failed to upload images to booking ${createdBookingId} Error occurred while creating a booking from ${
                        catalogItem.lmtId
                    } photos=${selectedPhotos.join(",")}`,
                },
            });
            throw error;
        }
    } catch (error) {
        Sentry.captureException(error, {
            extra: {
                message: `Failed to add item to cart. Error occurred while creating a booking from ${catalogItem.id}`,
            },
        });
        EventEmitter.emit({
            name: "showToastMessage",
            params: {
                type: "error",
                message: `Error occurred while creating a booking from ${catalogItem.name}`,
            },
        });
    }
}

async function createOneOffBooking(
    request: Request,
    bookings: Booking[],
    posthog: PostHog | null,
    createBooking: (payload: CreateBookingApiPayload) => Promise<{ bookingId: string }>,
    onSuccess: (bookingId: string) => void,
    input?: CreateOneOffBookingInput,
) {
    if (!request) return;

    const { details, photoUrls, recommendation } = input ?? {};

    try {
        const { bookingId } = await createBooking({
            requestId: request.id,
            details,
        });

        if (photoUrls?.length) {
            try {
                await uploadImagesToBooking(bookingId, photoUrls);
            } catch (error) {
                Sentry.captureException(error, {
                    extra: {
                        message: `Failed to upload images to one-off booking ${bookingId}`,
                    },
                });
            }
        }

        const isFirstOption = bookings.length === 0;
        if (recommendation) {
            posthog?.capture(AnalyticsEventName.REQUEST_RECOMMENDATIONS_OPTION_ADDED, {
                request_id: request.id,
                option_id: recommendation.optionId,
                fit_score: recommendation.fitScore,
            });
        }
        if (isFirstOption) {
            posthog?.capture(AnalyticsEventName.REQUEST_FIRST_OPTION_ADDED, {
                request_id: request.id,
                source: recommendation ? "ai" : "manual",
            });
        }

        onSuccess(bookingId);
    } catch (error) {
        Sentry.captureException(error, {
            extra: { message: "Failed to create one-off booking" },
        });
        EventEmitter.emit({
            name: "showToastMessage",
            params: {
                type: "error",
                message: "Error occurred while creating the booking",
            },
        });
    }
}

const CategoryDropdownEntries: DropdownOption[] = [
    { value: null, label: OfferingCategoryUncategorizedKey },
    ...CategoriesWithUncategorized.options
        .filter((option) => option !== OfferingCategoryUncategorizedKey)
        .map((it) => ({
            value: it,
            label: categoryDisplayName(it, "en"),
        })),
];

type Props = {
    request: Request;
    currentAgent: Agent;
    memberTenant: Tenant;
};

const RequestView = function RequestView(props: Props) {
    const { request, currentAgent, memberTenant } = props;
    const history = useHistory();
    const posthog = usePostHog();
    const { mutateAsync: createBookingMutation } = usePost("createBooking");
    const { mutate: updateRequestMutation } = usePatch("updateRequest", {
        onError: (error) => {
            logErrorAndShowToast(error, error.error ?? "Failed to update request");
        },
    });
    const { mutate: changeRequestStatusMutation } = usePost("changeRequestStatus", {
        onError: (error) => {
            logErrorAndShowToast(
                error,
                error.error ?? "Failed to change request status",
            );
        },
    });
    const { mutateAsync: archiveRequestMutation } = usePost("archiveRequest", {
        onError: (error) => {
            logErrorAndShowToast(error, error.error ?? "Failed to archive request");
        },
    });
    const { mutateAsync: unarchiveRequestMutation } = usePost("unarchiveRequest", {
        onError: (error) => {
            logErrorAndShowToast(error, error.error ?? "Failed to unarchive request");
        },
    });
    const createBooking = useCallback(
        async (payload: CreateBookingApiPayload) =>
            createBookingMutation({ json: payload }),
        [createBookingMutation],
    );
    const patchRequest = useCallback(
        (requestId: string, updates: UpdateRequestRequest) => {
            updateRequestMutation({
                param: { id: requestId },
                json: updates,
            });
        },
        [updateRequestMutation],
    );
    const storeGooglePlaceLocation = useCallback(
        (requestId: string, place: GooglePlace) => {
            const googlePlaceLocation: RequestGooglePlaceLocation = {
                locationGoogleName: place.displayName?.text,
                locationGooglePlaceId: place.id,
                locationGoogleFormattedAddress: place.formattedAddress,
                locationGoogleAddressComponents: googlePlaceToAddressComponents(place),
                locationGoogleGeometryLat: place.location?.latitude,
                locationGoogleGeometryLng: place.location?.longitude,
            };

            const parse = RequestGooglePlaceLocation.safeParse(googlePlaceLocation);
            if (!parse.success) {
                Sentry.captureException(parse.error, {
                    extra: { message: "Failed to parse address components" },
                });
                return;
            }

            patchRequest(requestId, googlePlaceLocation);
        },
        [patchRequest],
    );
    const deleteGooglePlaceLocation = useCallback(
        (requestId: string) => {
            patchRequest(requestId, {
                locationGoogleName: null,
                locationGooglePlaceId: null,
                locationGoogleFormattedAddress: null,
                locationGoogleAddressComponents: null,
                locationGoogleGeometryLat: null,
                locationGoogleGeometryLng: null,
            });
        },
        [patchRequest],
    );

    const { data: bookings } = useGet(
        "bookings",
        { query: { requestId: request.id } },
        {
            select: (response) => response.bookings.map(mapApiBookingToBooking),
        },
    );

    const categoryMissing = request && !request.offeringCategory;

    const requestHasConfirmedBookings = (bookings || []).some(
        (it) => it.reservationStatus === "confirmed",
    );
    const requestHasCancelledBookings = (bookings || []).some(
        (it) => it.reservationStatus === "cancelled",
    );
    const requestDeadline =
        request?.nextDeadlineAt && request.nextDeadlineStep
            ? {
                  nextDeadlineAt: request.nextDeadlineAt,
                  nextDeadlineStep: request.nextDeadlineStep,
                  deadlineCreatedAt: request.deadlineCreatedAt,
              }
            : undefined;

    useEffect(() => {
        const addCartItemListener: EventListener = {
            name: "addCatalogCartItem",
            listener: (params) => {
                void addItemFromCatalogToCart(
                    params.item,
                    request,
                    bookings || [],
                    posthog,
                    createBooking,
                    params.recommendation,
                );
            },
        };

        const createOneOffBookingListener: EventListener = {
            name: "createOneOffBooking",
            listener: (params) => {
                void createOneOffBooking(
                    request,
                    bookings || [],
                    posthog,
                    createBooking,
                    (bookingId) => {
                        history.push(routes.requestBooking(request.id, bookingId));
                    },
                    params.input,
                );
            },
        };

        EventEmitter.addListener(addCartItemListener);
        EventEmitter.addListener(createOneOffBookingListener);

        return () => {
            EventEmitter.removeListener(addCartItemListener);
            EventEmitter.removeListener(createOneOffBookingListener);
        };
    }, [request, bookings, posthog, createBooking, history]);

    const archiveRequest = async (archive: boolean) => {
        if (!request) throw new Error("No active request available!");
        try {
            await (archive ? archiveRequestMutation : unarchiveRequestMutation)({
                param: { id: request.id },
            });
            if (archive) {
                history.push(`/member/${request.member}`);
            }
        } catch {
            // Error toast handled by mutation onError
        }
    };

    const hasConfirmedBookings = (bookings || []).some(
        (booking) => booking.reservationStatus === "confirmed",
    );

    const areSameDay = (
        startDate: string | null | undefined,
        endDate: string | null | undefined,
    ): boolean => (startDate && endDate ? startDate === endDate : false);

    const timeIsAfter = (
        startTime: string | null | undefined,
        endTime: string | null | undefined,
    ): boolean =>
        startTime && endTime ? timeToDate(startTime) > timeToDate(endTime) : false;

    const dateIsAfter = (
        startDate: string | null | undefined,
        endDate: string | null | undefined,
    ): boolean =>
        startDate && endDate ? new Date(startDate) > new Date(endDate) : false;

    let requestToCloseAt: Date | undefined;
    if (hasConfirmedBookings) {
        const confirmedBooking = bookings?.find(
            (booking) => booking.reservationStatus === "confirmed",
        );
        const bookingDate = confirmedBooking?.endTime ?? confirmedBooking?.startTime;
        requestToCloseAt = bookingDate ? fromTimestampToDate(bookingDate) : undefined;
    }

    if (!request) return <div />;

    const capture = deriveCaptureView({
        aiCaptureStatus: request.aiCaptureStatus,
        highlightedFields: request.highlightedFields,
        status: request.status,
    });

    const lockRequestDates = hasConfirmedBookings;

    const openCatalog = () => {
        const locationFields = request.locationGoogleAddressComponents
            ? addressComponentsToObject(request.locationGoogleAddressComponents)
            : undefined;
        const locationPoint = {
            latitude: request.locationGoogleGeometryLat ?? undefined,
            longitude: request.locationGoogleGeometryLng ?? undefined,
        };

        const locationFilter = addressFieldsAndLocationToFilter(
            locationFields,
            locationPoint,
        );

        const filter: SearchCatalogItemsQuery = {
            ...locationFilter,
            ...(request.offeringCategory
                ? { categories: [request.offeringCategory] }
                : {}),
            discoverableForTenant: memberTenant,
        };
        const locationSearchTerm = request.locationGoogleFormattedAddress ?? undefined;
        history.push(
            routes.catalogSearchInit(
                "service",
                filter,
                locationSearchTerm,
                undefined,
                request.id,
                undefined,
            ),
        );
    };

    const openRecommendationsCatalog = (items: RecommendationItem[]) => {
        if (items.length === 0) return;

        const ids = Array.from(
            new Set(
                items.filter((item) => item.source === "catalog").map((it) => it.id),
            ),
        );

        history.push(
            routes.catalogSearchInit(
                "recommendations",
                { discoverableForTenant: memberTenant },
                undefined,
                ids.length > 0 ? ids : undefined,
                request.id,
                undefined,
            ),
        );
    };

    const openBooking = (bookingId: string) => {
        history.push(routes.requestBooking(request.id, bookingId));
    };

    const requestInputFields = (
        <Lib.Flex.Column gap="value24">
            <h5>Internal to Albertine</h5>
            <Lib.Flex.Column gap="value8">
                <Lib.Flex.Row gap="value8">
                    <Label.Small.Light.N50 className="request-view__label-column">
                        Category
                    </Label.Small.Light.N50>
                    <AnalysingValue analysing={capture.isAnalysing}>
                        <Dropdown
                            optionList={CategoryDropdownEntries}
                            currentValue={request.offeringCategory}
                            onChange={(offeringCategory) => {
                                if (offeringCategory == null) return;
                                if (!isTypeOf(offeringCategory, OfferingCategory))
                                    return;
                                patchRequest(request.id, { offeringCategory });
                            }}
                            highlight={request.highlightedFields?.includes(
                                "offeringCategory",
                            )}
                        />
                    </AnalysingValue>
                </Lib.Flex.Row>

                <Lib.Flex.Row gap="value8">
                    <Label.Small.Light.N50 className="request-view__label-column">
                        City or area
                    </Label.Small.Light.N50>
                    <AnalysingValue analysing={capture.isAnalysing}>
                        <GooglePlaceInputField
                            placeholder="City or area"
                            searchFor="politicalRegions"
                            displayLocationPin
                            highlight={request.highlightedFields?.includes(
                                "locationGooglePlaceId",
                            )}
                            googlePlaceId={
                                hasGooglePlaceLocation(request) &&
                                request.locationGoogleFormattedAddress
                                    ? request.locationGoogleFormattedAddress
                                    : undefined
                            }
                            fields={[
                                "id",
                                "displayName",
                                "formattedAddress",
                                "addressComponents",
                                "location",
                            ]}
                            onSelect={(place) => {
                                storeGooglePlaceLocation(request.id, place);
                            }}
                            onClear={() => {
                                deleteGooglePlaceLocation(request.id);
                            }}
                        />
                    </AnalysingValue>
                </Lib.Flex.Row>

                <Lib.Flex.Row gap="value8">
                    <Label.Small.Light.N50 className="request-view__label-column">
                        Budget
                    </Label.Small.Light.N50>
                    <AnalysingValue analysing={capture.isAnalysing}>
                        <Input.TextField
                            placeholder="Budget"
                            value={request.budget || ""}
                            highlight={request.highlightedFields?.includes("budget")}
                            onBlur={(budget) => {
                                void patchRequest(request.id, { budget });
                            }}
                        />
                    </AnalysingValue>
                </Lib.Flex.Row>

                <Lib.Flex.Row gap="value8">
                    <Label.Small.Light.N50 className="request-view__label-column">
                        First choice
                    </Label.Small.Light.N50>
                    <AnalysingValue analysing={capture.isAnalysing}>
                        <Input.TextField
                            placeholder="Name of the venue"
                            value={request.firstChoice ?? ""}
                            highlight={request.highlightedFields?.includes(
                                "firstChoice",
                            )}
                            onBlur={(firstChoice) => {
                                void patchRequest(request.id, { firstChoice });
                            }}
                        />
                    </AnalysingValue>
                </Lib.Flex.Row>

                <Lib.Flex.Row gap="value8">
                    <Label.Small.Light.N50 className="request-view__label-column">
                        Assigned to
                    </Label.Small.Light.N50>
                    <AnalysingValue analysing={capture.isAnalysing}>
                        <AgentDropdown
                            assignedAgentId={request.assignedTo}
                            currentAgentId={currentAgent.id}
                            onChange={(assignedTo) =>
                                void patchRequest(request.id, {
                                    assignedTo,
                                })
                            }
                        />
                    </AnalysingValue>
                </Lib.Flex.Row>

                <Lib.Flex.Row gap="value8">
                    <Label.Small.Light.N50 className="request-view__label-column">
                        Source
                    </Label.Small.Light.N50>
                    <AnalysingValue analysing={capture.isAnalysing}>
                        <Dropdown
                            entries={Object.entries(RequestSourceMap)}
                            currentValue={request.source}
                            onChange={(source) => {
                                if (isTypeOf(source, RequestSource)) {
                                    void patchRequest(request.id, {
                                        source,
                                    });
                                }
                            }}
                        />
                    </AnalysingValue>
                </Lib.Flex.Row>

                <Lib.Flex.Row gap="value8">
                    <Label.Small.Light.N50 className="request-view__label-column">
                        Channel
                    </Label.Small.Light.N50>
                    <AnalysingValue analysing={capture.isAnalysing}>
                        <Dropdown
                            entries={Object.entries(PREFERRED_CHANNEL_LABELS)}
                            currentValue={request.sourceChannel ?? "app"}
                            onChange={(channel) => {
                                if (
                                    isTypeOf(
                                        channel,
                                        MemberPreferredCommunicationChannel,
                                    )
                                ) {
                                    void patchRequest(request.id, {
                                        sourceChannel: channel,
                                    });
                                }
                            }}
                        />
                    </AnalysingValue>
                </Lib.Flex.Row>
            </Lib.Flex.Column>
            <h5>Visible to member</h5>
            <Lib.Flex.Column gap="value8">
                <Lib.Flex.Row gap="value8">
                    <Label.Small.Light.N50 className="request-view__label-column">
                        Start time
                    </Label.Small.Light.N50>
                    <AnalysingValue analysing={capture.isAnalysing}>
                        {lockRequestDates ? (
                            <Label.Medium.Normal.N10 className="request-view__static-input-value">
                                {request.startDate ? (
                                    <FormattedDateTime.Default
                                        timestamp={timestampFromDateTimeStrings(
                                            request.startDate,
                                            request.startTime,
                                        )}
                                    />
                                ) : (
                                    "-"
                                )}
                            </Label.Medium.Normal.N10>
                        ) : (
                            <EditableLocalDateInput
                                initialDateValue={request.startDate}
                                initialTimeValue={request.startTime}
                                onDateChange={(startDate) => {
                                    const startDateIsSetAfterEndDateTime =
                                        (areSameDay(startDate, request.endDate) &&
                                            timeIsAfter(
                                                request.startTime,
                                                request.endTime,
                                            )) ||
                                        dateIsAfter(startDate, request.endDate);
                                    if (startDateIsSetAfterEndDateTime) {
                                        void patchRequest(request.id, {
                                            startDate,
                                            endDate: null,
                                            endTime: null,
                                        });
                                    } else {
                                        void patchRequest(request.id, {
                                            startDate,
                                        });
                                    }
                                }}
                                onTimeChange={(startTime) => {
                                    const startTimeIsSetAfterEndTime =
                                        areSameDay(
                                            request.startDate,
                                            request.endDate,
                                        ) && timeIsAfter(startTime, request.endTime);
                                    if (startTimeIsSetAfterEndTime) {
                                        void patchRequest(request.id, {
                                            startTime,
                                            endTime: null,
                                        });
                                    } else {
                                        void patchRequest(request.id, {
                                            startTime,
                                        });
                                    }
                                }}
                                highlightDate={request.highlightedFields?.includes(
                                    "startDate",
                                )}
                                highlightTime={request.highlightedFields?.includes(
                                    "startTime",
                                )}
                            />
                        )}
                    </AnalysingValue>
                </Lib.Flex.Row>

                <Lib.Flex.Row gap="value8">
                    <Label.Small.Light.N50 className="request-view__label-column">
                        End time
                    </Label.Small.Light.N50>
                    <AnalysingValue analysing={capture.isAnalysing}>
                        {lockRequestDates ? (
                            <Label.Medium.Normal.N10 className="request-view__static-input-value">
                                {request.endDate ? (
                                    <FormattedDateTime.Default
                                        timestamp={timestampFromDateTimeStrings(
                                            request.endDate,
                                            request.endTime,
                                        )}
                                    />
                                ) : (
                                    "-"
                                )}
                            </Label.Medium.Normal.N10>
                        ) : (
                            <EditableLocalDateInput
                                initialDateValue={request.endDate}
                                initialTimeValue={request.endTime}
                                minDateValue={request.startDate}
                                minTimeValue={request.startTime}
                                onDateChange={(endDate) => {
                                    void patchRequest(request.id, { endDate });
                                }}
                                onTimeChange={(endTime) => {
                                    void patchRequest(request.id, { endTime });
                                }}
                                highlightDate={request.highlightedFields?.includes(
                                    "endDate",
                                )}
                                highlightTime={request.highlightedFields?.includes(
                                    "endTime",
                                )}
                            />
                        )}
                    </AnalysingValue>
                </Lib.Flex.Row>
                <Lib.Flex.Row gap="value8">
                    <Label.Small.Light.N50 className="request-view__label-column">
                        Guests
                    </Label.Small.Light.N50>
                    <AnalysingValue analysing={capture.isAnalysing}>
                        <Input.TextField
                            placeholder="Number of guests"
                            value={request.numberOfGuests?.toString() || ""}
                            isValid={(value) => {
                                if (!value || value.length === 0) {
                                    return true;
                                }

                                const { success } = NumberOfGuests.safeParse(value);

                                return success;
                            }}
                            highlight={request.highlightedFields?.includes(
                                "numberOfGuests",
                            )}
                            onBlur={(numberOfGuestsInput) => {
                                const parseResult =
                                    NumberOfGuests.safeParse(numberOfGuestsInput);

                                const numberOfGuests = parseResult.success
                                    ? parseInt(parseResult.data, 10)
                                    : null;

                                void patchRequest(request.id, {
                                    numberOfGuests,
                                });
                            }}
                        />
                    </AnalysingValue>
                </Lib.Flex.Row>
            </Lib.Flex.Column>

            <hr />

            <Proposal
                request={request}
                bookings={bookings || []}
                onOptionClick={(booking) => {
                    openBooking(booking.id);
                }}
                onAddNewOptionClick={() => {
                    openCatalog();
                }}
            />

            <RecommendButton
                requestId={request.id}
                hasRequestLocation={
                    request.locationGoogleGeometryLat != null &&
                    request.locationGoogleGeometryLng != null
                }
                onOpenRecommendations={(items) => {
                    openRecommendationsCatalog(items);
                }}
            />

            <hr />
            <h5>Internal notes</h5>
            {request?.notes && (
                <EditableTextarea
                    placeholder="Type your internal notes here..."
                    initialValue={request?.notes}
                    internalIndicator
                    className="request-view__internal-notes"
                    onChange={(notes) => patchRequest(request.id, { notes })}
                />
            )}
            {request && (
                <InternalNotes
                    request={request.id}
                    conversation={request.conversation}
                    hasOldInternalNotes={!!request.notes}
                />
            )}
        </Lib.Flex.Column>
    );

    return (
        <div
            className={classNames("request-view", {
                archived: request.archived,
            })}
        >
            <Lib.Flex.Column className="request-view__editables" gap="value24">
                <Lib.Flex.Column gap="value8">
                    <AnalysingValue analysing={capture.isAnalysing} title>
                        <Input.TextArea
                            placeholder={defaultRequestTitle}
                            value={request.title || ""}
                            rows={1}
                            className="textarea__heading request-view__title"
                            onBlur={(title) => patchRequest(request.id, { title })}
                            maxLength={1024}
                            autoResize
                            trim
                            removeLineBreaks
                            highlight={request.highlightedFields?.includes("title")}
                        />
                    </AnalysingValue>
                    <Lib.Flex.Row
                        className="request-view__scroll-into-request pictogram-icon--hover"
                        justifyContent="space-between"
                        onClick={async () => {
                            if (request.createdFromMessage) {
                                EventEmitter.emit({
                                    name: "scrollChatIntoMessage",
                                    params: {
                                        messageId: request.createdFromMessage,
                                    },
                                });
                                history.replace(
                                    `/request/${request.id}/message/${request.createdFromMessage}`,
                                );
                            } else {
                                const getRequestFirstMessage = await getDocs(
                                    queryMessageWithRequest(
                                        request.conversation,
                                        request.id,
                                        "first",
                                    ),
                                );
                                const requestFirstMessageId =
                                    getRequestFirstMessage.docs[0].id;
                                if (requestFirstMessageId) {
                                    EventEmitter.emit({
                                        name: "scrollChatIntoMessage",
                                        params: {
                                            messageId: requestFirstMessageId,
                                        },
                                    });

                                    history.replace(
                                        `/request/${request.id}/message/${requestFirstMessageId}`,
                                    );
                                }
                            }
                        }}
                    >
                        <Label.Small.Light.N60>
                            #{request.ticketNumber} - Created:{" "}
                            <FormattedDateTime.Default timestamp={request.createdAt} />
                        </Label.Small.Light.N60>
                        <Tooltip
                            label="Go to first message of the request"
                            placement="left"
                        >
                            <PictogramIcon.ArrowUpward.Small.N99 />
                        </Tooltip>
                    </Lib.Flex.Row>

                    <div
                        className={cn(
                            "flex w-full flex-col",
                            capture.isAnalysing &&
                                "pointer-events-none select-none opacity-60",
                        )}
                        title={capture.isAnalysing ? "Wait for analysis" : undefined}
                    >
                        <RequestStatusSelector
                            request={request}
                            closeAt={requestToCloseAt}
                            deadline={requestDeadline}
                            requestHasConfirmedBookings={requestHasConfirmedBookings}
                            requestHasCancelledBookings={requestHasCancelledBookings}
                            beforeOnClick={() => {
                                if (capture.isAnalysing) return false;
                                if (request.status === "draft" && categoryMissing) {
                                    window.alert("Please select the category first");
                                    return false;
                                }
                                return true;
                            }}
                            onChange={(change) => {
                                changeRequestStatusMutation({
                                    param: { id: request.id },
                                    json: change,
                                });
                            }}
                        />
                    </div>

                    {request.memberFeedbackTimestamp && (
                        <>
                            <MemberRating
                                maxRating={5}
                                userRating={request.memberRating ?? 0}
                                message={request.memberFeedback}
                                timestamp={request.memberFeedbackTimestamp}
                            />
                            <hr />
                        </>
                    )}

                    {request.status === "open" && hasConfirmedBookings && (
                        <AlertNotification>
                            Please remember to mark the request as confirmed.
                        </AlertNotification>
                    )}

                    {capture.showAiReview && (
                        <div className="rounded-lg bg-blue-50 px-3 py-2">
                            <p className="font-medium text-blue-900 text-xs leading-label">
                                Request details captured with AI. Review before
                                actioning.
                            </p>
                        </div>
                    )}

                    {capture.isFailed && (
                        <div className="rounded-lg bg-blue-50 px-3 py-2">
                            <p className="font-medium text-blue-900 text-xs leading-label">
                                Automatic capture failed.
                            </p>
                        </div>
                    )}
                </Lib.Flex.Column>

                {requestInputFields}
            </Lib.Flex.Column>
            <hr />
            <Lib.Flex.Column gap="value8">
                <h4>Actions</h4>

                {request.archived ? (
                    <Button.E90
                        onClick={() => {
                            if (
                                window.confirm(
                                    "Are you sure you want to unarchive request?",
                                )
                            ) {
                                void archiveRequest(false);
                            }
                        }}
                    >
                        <Icon.RestoreFromTrash.Small.E10 />
                        Unarchive
                    </Button.E90>
                ) : (
                    <Button.E90
                        onClick={() => {
                            if (request.status === "open") {
                                alert(
                                    "You cannot archive an open request. Please close the request first.",
                                );
                                return;
                            }
                            if (
                                window.confirm(
                                    "Are you sure you want to archive this request?",
                                )
                            ) {
                                void archiveRequest(true);
                            }
                        }}
                    >
                        <Icon.Delete.Small.E10 />
                        Archive
                    </Button.E90>
                )}
            </Lib.Flex.Column>
        </div>
    );
};

export default RequestView;
